import { useState, type VoidFunctionComponent } from 'react';

import { DISABLE_MANUAL_DEPENDENCIES_TEST_VALIDITY, FUZZY_DEP_SEARCH } from '../../../../shared/featureFlags/flagNames';
import {
  commonLinuxArchs,
  FETCHER_DROPDOWN_OPTIONS,
  isLinuxFetcher,
  linuxDistrosByFetcher,
  type ThirdPartyFetcher,
} from '../../../../shared/fetchers';
import Locator from '../../../../shared/Locator';
import { isTypeaheadable, TYPEAHEAD_FETCHERS } from '../../../../shared/registrySearch/capabilities';
import { isFilled } from '../../../../shared/utils/isFilled';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { selectOptionFactory } from '../../../utils';
import { LabeledSelect, LabeledTextInput } from '../../generic';
import Button, { LoaderButton } from '../../generic/Button';
import Tooltip from '../../generic/Tooltip';
import type { ManuallyAddedDependency, ManuallyAddedDependencyRequest } from '../../../../typings/api';

import useCheckRevision, { isCheckRevisionTimedOut } from './hooks/useCheckRevision';
import { PackageNameAutocomplete } from './PackageNameAutocomplete';
import { PackageVersionAutocomplete } from './PackageVersionAutocomplete';
import TestDependencyCard from './TestDependencyCard';

import styles from './SearchDependency.module.less';

const COMMON_LINUX_ARCH_OPTIONS = commonLinuxArchs.map((linuxArch) => selectOptionFactory(linuxArch));

type Props = {
  /**
   * Whether the dependency is being added as a vendored dependency.
   */
  isVendored?: boolean;
  /**
   * Locator of the existing dependency when overwriting.
   */
  overwriteDependencyLocator?: string;

  /**
   * Callback to save the dependency.
   * NOTE: The return value here is typed as "unknown" because React Query's mutateAsync
   * returns whatever the mutation function returns but we don't care about it.
   */
  saveDependency?: (params: ManuallyAddedDependencyRequest) => Promise<unknown>;
  /**
   * Whether the dependency is currently being saved.
   */
  isSaving?: boolean;

  /**
   * Callback when the drawer is closed.
   */
  onClose?: () => void;
};

/**
 * Renders the "Search" tab of the Add Dependency drawer, allowing users to
 * search for an existing package and add it as a dependency to their project.
 */
const SearchDependency: VoidFunctionComponent<Props> = ({
  isVendored,
  overwriteDependencyLocator,
  saveDependency,
  isSaving,
  onClose,
}) => {
  const [filePath, setFilePath] = useState<string>('');
  const [packageManager, setPackageManager] = useState<ThirdPartyFetcher | undefined>(undefined);
  const [name, setName] = useState<string>('');
  // The version lookup keys off this *committed* package name — set when the user picks a
  // typeahead result or blurs the name field — rather than the live keystroke text, so we don't
  // fire an exact-package lookup for every partial name the user types.
  const [committedName, setCommittedName] = useState<string>('');
  const [version, setVersion] = useState<string>('');

  const [distro, setDistro] = useState<string | undefined>(undefined);
  const [release, setRelease] = useState<string>('');
  const [arch, setArch] = useState<string | undefined>(undefined);

  const resetLinuxFields = () => {
    setDistro(undefined);
    setRelease('');
    setArch(undefined);
  };

  const isSearchingForLinuxPackage = packageManager && isLinuxFetcher(packageManager);

  const canSaveVendored = isVendored ? isFilled(filePath) : true;
  const canSaveLinux = isSearchingForLinuxPackage ? !!distro && isFilled(release) && !!arch : true;
  const canSave = !!packageManager && isFilled(name) && isFilled(version) && canSaveVendored && canSaveLinux;

  const {
    mutate: checkRevision,
    isPending: isCheckingRevision,
    data: checkRevisionResponse,
    error: checkRevisionError,
  } = useCheckRevision();
  const hasCheckRevisionTimedOut = isCheckRevisionTimedOut(checkRevisionResponse);
  const checkRevisionMetadata = hasCheckRevisionTimedOut ? undefined : checkRevisionResponse;

  const isFuzzyDepSearchEnabled = useIsFeatureEnabled(FUZZY_DEP_SEARCH);
  const useTypeahead = isFuzzyDepSearchEnabled && !!packageManager && TYPEAHEAD_FETCHERS.includes(packageManager);
  // Within a typeahead-supported fetcher, gate the dropdown on whether the current input shape
  // matches a host our backend can autocomplete (e.g., for `git`, only `github.com/...` today).
  // When false, the field still accepts typed input but skips the fetch and keeps the menu
  // closed — manual-entry mode for shapes we can't search.
  const isTypeaheadShapeMatch = useTypeahead && !!packageManager && isTypeaheadable(packageManager, name);

  const isManualDependenciesTestValidityEnabled = !useIsFeatureEnabled(DISABLE_MANUAL_DEPENDENCIES_TEST_VALIDITY);
  const shouldUseTestWorkflow = isManualDependenciesTestValidityEnabled && !!packageManager;
  const [hasTested, setHasTested] = useState(false);

  // Gate on `hasTested` so the card hides when any input handler resets the testing state.
  // Without this, react-query's mutation result (`checkRevisionResponse` / `checkRevisionError`)
  // persists until the next mutation runs, leaving stale test results visible for an edited
  // dependency that hasn't been re-tested.
  const shouldShowTestDependencyCard = hasTested && (isCheckingRevision || checkRevisionError || checkRevisionResponse);

  const isTestingOrSaving = isSaving || (shouldUseTestWorkflow && isCheckingRevision);

  const onTest = async () => {
    // NOTE: This will only ever be called if canSave is true but TS doesn't know that.
    if (!canSave) {
      return;
    }

    const locatorParts = {
      fetcher: packageManager,
      package: name,
      revision: version,
    };

    if (distro && release && arch) {
      locatorParts.package = `${name}#${distro}#${release}`;
      locatorParts.revision = `${arch}#${version}`;
    }

    const locator = new Locator(locatorParts);

    setHasTested(true);
    checkRevision(locator.toString());
  };

  const onSave = async () => {
    // NOTE: This will only ever be called if canSave is true but TS doesn't know that.
    if (!canSave) {
      return;
    }

    const newDependency: ManuallyAddedDependency = {
      kind: 'locator',
      fetcher: packageManager,
      packageName: name.trim(),
      revision: version.trim(),
      vendoredPath: filePath.trim() || undefined,
      distro: distro || undefined,
      distroRelease: release.trim() || undefined,
      arch: arch || undefined,
    };

    if (overwriteDependencyLocator) {
      await saveDependency?.({
        kind: 'replace',
        overwrittenLocator: overwriteDependencyLocator,
        newDependency,
      });
    } else {
      await saveDependency?.({
        kind: 'new',
        newDependency,
      });
    }

    onClose?.();
  };

  const testOrSaveButtonText = (shouldUseTestWorkflow && !hasTested) ? 'Test Dependency' : 'Save';
  const shouldShowGitTestTooltip = packageManager === 'git' && shouldUseTestWorkflow && !hasTested;
  const onTestOrSave = async () => {
    if (shouldUseTestWorkflow && !hasTested) {
      await onTest();
    } else {
      await onSave();
    }
  };

  return (
    <section>
      <header>
        <p className={styles.overview}>
          Search for an existing package and add it as a {isVendored ? 'vendored' : 'manual'} dependency to your project.
        </p>
      </header>

      <main className={styles.main}>
        <h3 className={styles.title}>
          Package Details
        </h3>
        {isVendored && (
          <div className={styles.field}>
            <LabeledTextInput
              id="search-dependency-file-path"
              size="small"
              label="File Path"
              placeholder="Path to the vendored dependency"
              value={filePath}
              onChange={(value) => {
                setFilePath(value);
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
            <p className={styles.notes}>Add the path to the vendored dependency.</p>
          </div>
        )}

        <div className={styles.field}>
          <LabeledSelect
            id="search-dependency-package-manager"
            label="Package Manager"
            placeholder="Select package manager..."
            options={FETCHER_DROPDOWN_OPTIONS}
            value={FETCHER_DROPDOWN_OPTIONS.find((opt) => opt.value === packageManager)}
            onChange={(opt) => {
              setPackageManager(opt?.value);
              // Reset name/version when fetcher changes — names and versions don't carry across registries.
              setName('');
              setCommittedName('');
              setVersion('');
              // Linux fields are scoped to the fetcher (distros differ between deb/rpm/...) and
              // are otherwise hidden on non-Linux fetchers; clear them so a fetcher switch can't
              // carry stale (or invalid) Linux state into the save payload.
              resetLinuxFields();
              setHasTested(false);
            }}
            isDisabled={isTestingOrSaving}
            isRequired
          />
          <p className={styles.notes}>This tells FOSSA where to grab the same package for our analysis.</p>
        </div>

        <div className={styles.field}>
          {useTypeahead && packageManager ? (
            <PackageNameAutocomplete
              id="search-dependency-name"
              fetcher={packageManager}
              value={name}
              isMenuEnabled={isTypeaheadShapeMatch}
              onChange={(newName, fromTypeahead) => {
                setName(newName);
                // Changing the name invalidates the version (versions are scoped to a package).
                setVersion('');
                resetLinuxFields();
                setHasTested(false);
                // Commit immediately on a typeahead pick; while the user is still typing, clear the
                // commit so version lookups wait until the name is committed again (on blur).
                setCommittedName(fromTypeahead ? newName : '');
              }}
              onBlur={() => setCommittedName(name.trim())}
              isDisabled={isTestingOrSaving}
              isRequired
            />
          ) : (
            <LabeledTextInput
              id="search-dependency-name"
              size="small"
              label="Name"
              placeholder="Enter a package name"
              value={name}
              onChange={(value) => {
                setName(value);
                // Mirror the autocomplete path: versions are scoped to a package, so changing
                // the name invalidates the version too.
                setVersion('');
                resetLinuxFields();
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
          )}
        </div>

        <div className={styles.field}>
          {useTypeahead && packageManager ? (
            <PackageVersionAutocomplete
              id="search-dependency-version"
              fetcher={packageManager}
              packageName={committedName}
              isMenuEnabled={isTypeaheadShapeMatch}
              value={version}
              onChange={(newVersion) => {
                setVersion(newVersion);
                resetLinuxFields();
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
          ) : (
            <LabeledTextInput
              id="search-dependency-version"
              size="small"
              label="Version"
              placeholder="Enter a version number, eg. v1 or 1.0"
              value={version}
              onChange={(value) => {
                setVersion(value);
                resetLinuxFields();
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
          )}
        </div>
        {isSearchingForLinuxPackage && (
          <div className={styles.linuxFields}>
            <LabeledSelect
              id="search-dependency-distro"
              label="Distro"
              placeholder="Select distribution..."
              options={linuxDistrosByFetcher[packageManager]}
              value={linuxDistrosByFetcher[packageManager]?.find((opt) => opt.value === distro) ?? null}
              onChange={(opt) => {
                setDistro(opt?.value);
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
            <LabeledTextInput
              id="search-dependency-release"
              size="small"
              label="Release"
              placeholder="24.04"
              value={release}
              onChange={(value) => {
                setRelease(value);
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
            <LabeledSelect
              id="search-dependency-arch"
              label="Architecture"
              placeholder="Select architecture..."
              options={COMMON_LINUX_ARCH_OPTIONS}
              value={COMMON_LINUX_ARCH_OPTIONS.find((opt) => opt.value === arch) ?? null}
              onChange={(opt) => {
                setArch(opt?.value);
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
          </div>
        )}
        {shouldShowTestDependencyCard && (
          <TestDependencyCard
            metadata={checkRevisionMetadata}
            isLoading={isCheckingRevision}
            isIndeterminate={hasCheckRevisionTimedOut}
            isInvalid={!!checkRevisionError}
          />
        )}
      </main>

      <footer className={styles.footer}>
        <Button type="secondary" onClick={() => onClose?.()}>Cancel</Button>
        <Tooltip
          id="search-dependency-test-tooltip"
          tooltip="Testing a Git dependency can take up to a minute."
          showTooltip={shouldShowGitTestTooltip}
        >
          <span className="inline-block">
            <LoaderButton
              type="primary"
              onClick={onTestOrSave}
              isDisabled={!canSave || isTestingOrSaving}
              isLoading={isTestingOrSaving}
            >
              {testOrSaveButtonText}
            </LoaderButton>
          </span>
        </Tooltip>
      </footer>
    </section>
  );
};

export default SearchDependency;
